/* ============================================================
   NOELMA TELES — NATIVE BUNDLE
   File: assets/nt-bundle.js

   Handles:
   - Product siblings as colors
   - Dynamic product options
   - Variant resolution
   - Availability
   - Kit 2 / Kit 3 step navigation
   - Inventory validation
   - Multi-item Ajax Cart add
   - Discount code application
   - Palo Alto cart drawer refresh
   ============================================================ */

(() => {
  'use strict';

  if (customElements.get('nt-bundle')) return;

  class NTBundle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ntInitialized === 'true') return;
      this.dataset.ntInitialized = 'true';

      this.dataElement = this.querySelector('[data-nt-bundle-data]');
      this.configurator = this.querySelector('[data-nt-configurator]');

      /*
       * Quantity 1 only needs the offer selector.
       * It does not render the kit configurator or JSON data.
       */
      if (!this.configurator || !this.dataElement) return;

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (error) {
        console.error('[NT Bundle] Invalid bundle JSON:', error);
        this.showConfigurationError();
        return;
      }

      this.bundle = this.data.bundle || {};
      this.products = Array.isArray(this.data.products)
        ? this.data.products
        : [];

      this.quantity = Number(this.bundle.quantity || 0);

      if (!this.quantity || !this.products.length) {
        this.showConfigurationError();
        return;
      }

      this.steps = Array.from(
        this.querySelectorAll('[data-nt-step]')
      );

      this.progressItems = Array.from(
        this.querySelectorAll('[data-nt-progress-item]')
      );

      this.errorsElement = this.querySelector(
        '[data-nt-bundle-errors]'
      );

      this.currentStep = 0;
      this.maxUnlockedStep = 0;
      this.isSubmitting = false;

      this.state = Array.from(
        { length: this.quantity },
        () => this.createEmptyPieceState()
      );

      this.onClick = this.onClick.bind(this);

      this.addEventListener('click', this.onClick);

      this.initialize();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    /* ========================================================
       INITIALIZATION
       ======================================================== */

    initialize() {
      this.steps.forEach((step, index) => {
        this.renderColors(index);
      });

      this.showStep(0, false);
      this.updateUI();
    }

    createEmptyPieceState() {
      return {
        product: null,
        selectedOptions: [],
        variant: null,
        complete: false
      };
    }

    /* ========================================================
       CLICK ROUTER
       ======================================================== */

    onClick(event) {
      const colorButton = event.target.closest(
        '[data-nt-color-index]'
      );

      if (colorButton && this.contains(colorButton)) {
        event.preventDefault();

        if (colorButton.disabled) return;

        const stepIndex = Number(
          colorButton.dataset.ntStepIndex
        );

        const productIndex = Number(
          colorButton.dataset.ntColorIndex
        );

        this.selectProduct(stepIndex, productIndex);
        return;
      }

      const optionButton = event.target.closest(
        '[data-nt-option-value]'
      );

      if (optionButton && this.contains(optionButton)) {
        event.preventDefault();

        if (optionButton.disabled) return;

        const stepIndex = Number(
          optionButton.dataset.ntStepIndex
        );

        const optionIndex = Number(
          optionButton.dataset.ntOptionIndex
        );

        const value = optionButton.dataset.ntOptionValue;

        this.selectOption(
          stepIndex,
          optionIndex,
          value
        );

        return;
      }

      const nextButton = event.target.closest(
        '[data-nt-next]'
      );

      if (nextButton && this.contains(nextButton)) {
        event.preventDefault();

        const stepIndex = this.getStepIndex(nextButton);

        if (
          stepIndex !== -1 &&
          this.state[stepIndex]?.complete
        ) {
          this.goToNextStep(stepIndex);
        }

        return;
      }

      const backButton = event.target.closest(
        '[data-nt-back]'
      );

      if (backButton && this.contains(backButton)) {
        event.preventDefault();

        const stepIndex = this.getStepIndex(backButton);

        if (stepIndex > 0) {
          this.showStep(stepIndex - 1);
        }

        return;
      }

      const progressButton = event.target.closest(
        '[data-nt-progress-item]'
      );

      if (
        progressButton &&
        this.contains(progressButton)
      ) {
        event.preventDefault();

        if (progressButton.disabled) return;

        const index =
          Number(progressButton.dataset.ntProgressItem) - 1;

        if (
          index >= 0 &&
          index <= this.maxUnlockedStep
        ) {
          this.showStep(index);
        }

        return;
      }

      const submitButton = event.target.closest(
        '[data-nt-bundle-add]'
      );

      if (submitButton && this.contains(submitButton)) {
        event.preventDefault();

        if (
          submitButton.disabled ||
          this.isSubmitting
        ) {
          return;
        }

        this.submitBundle();
      }
    }

    getStepIndex(element) {
      const step = element.closest('[data-nt-step]');

      if (!step) return -1;

      return Number(step.dataset.ntStep) - 1;
    }

    /* ========================================================
       COLOR / SIBLING PRODUCTS
       ======================================================== */

    renderColors(stepIndex) {
      const step = this.steps[stepIndex];
      if (!step) return;

      const holder = step.querySelector(
        '[data-nt-colors]'
      );

      if (!holder) return;

      holder.innerHTML = '';

      const piece = this.state[stepIndex];

      this.products.forEach((product, productIndex) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'nt-bundle__color';

        button.dataset.ntColorIndex = String(productIndex);
        button.dataset.ntStepIndex = String(stepIndex);

        const label =
          product.colorLabel ||
          product.title ||
          `Cor ${productIndex + 1}`;

        button.setAttribute(
          'aria-label',
          `Selecionar ${label}`
        );

        button.title = label;

        if (!product.available) {
          button.disabled = true;
          button.classList.add('is-unavailable');
        }

        if (
          piece.product &&
          String(piece.product.id) === String(product.id)
        ) {
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.setAttribute('aria-pressed', 'false');
        }

        if (product.image) {
          const image = document.createElement('img');

          image.className =
            'nt-bundle__color-image';

          image.src = product.image;
          image.alt = '';
          image.width = 52;
          image.height = 52;
          image.loading = 'lazy';

          button.appendChild(image);
        } else {
          const swatch = document.createElement('span');

          swatch.className =
            'nt-bundle__color-swatch';

          if (
            typeof product.colorValue === 'string' &&
            product.colorValue.trim()
          ) {
            swatch.style.setProperty(
              '--nt-swatch',
              product.colorValue
            );
          }

          button.appendChild(swatch);
        }

        const check = document.createElement('span');

        check.className =
          'nt-bundle__color-check';

        check.setAttribute('aria-hidden', 'true');

        button.appendChild(check);

        holder.appendChild(button);
      });

      this.updateSelectedColorLabel(stepIndex);
    }

    selectProduct(stepIndex, productIndex) {
      this.clearError();

      const product = this.products[productIndex];

      if (!product || !product.available) return;

      const piece = this.state[stepIndex];

      /*
       * Changing the color/product resets all variant choices.
       */
      piece.product = product;
      piece.variant = null;
      piece.complete = false;

      const options = Array.isArray(product.options)
        ? product.options
        : [];

      piece.selectedOptions = Array(
        options.length
      ).fill(null);

      /*
       * Options with only one value do not need customer input.
       * Select them automatically.
       */
      options.forEach((option, optionIndex) => {
        const values = this.normalizeValues(option.values);

        if (values.length === 1) {
          piece.selectedOptions[optionIndex] =
            values[0];
        }
      });

      this.renderColors(stepIndex);
      this.renderOptions(stepIndex);
      this.resolvePiece(stepIndex);
      this.updateUI();
    }

    updateSelectedColorLabel(stepIndex) {
      const step = this.steps[stepIndex];

      if (!step) return;

      const label = step.querySelector(
        '[data-nt-selected-color]'
      );

      if (!label) return;

      const product = this.state[stepIndex]?.product;

      label.textContent = product
        ? product.colorLabel || product.title || ''
        : '';
    }

    /* ========================================================
       DYNAMIC PRODUCT OPTIONS
       ======================================================== */

    renderOptions(stepIndex) {
      const step = this.steps[stepIndex];

      if (!step) return;

      const holder = step.querySelector(
        '[data-nt-options]'
      );

      if (!holder) return;

      holder.innerHTML = '';

      const piece = this.state[stepIndex];
      const product = piece.product;

      if (!product) return;

      const options = Array.isArray(product.options)
        ? product.options
        : [];

      options.forEach((option, optionIndex) => {
        const values = this.normalizeValues(option.values);

        /*
         * Single-value options are already selected
         * automatically and do not need a selector.
         */
        if (values.length <= 1) return;

        const group = document.createElement('div');

        group.className =
          'nt-bundle__option-group';

        group.dataset.ntOptionGroup =
          String(optionIndex);

        const heading = document.createElement('div');

        heading.className =
          'nt-bundle__option-heading';

        const name = document.createElement('span');

        name.className =
          'nt-bundle__option-name';

        name.textContent =
          option.name || `Opção ${optionIndex + 1}`;

        const current = document.createElement('span');

        current.className =
          'nt-bundle__option-current';

        current.textContent =
          piece.selectedOptions[optionIndex] || '';

        heading.appendChild(name);
        heading.appendChild(current);

        group.appendChild(heading);

        const valuesHolder =
          document.createElement('div');

        valuesHolder.className =
          'nt-bundle__option-values';

        values.forEach((value) => {
          const button =
            document.createElement('button');

          button.type = 'button';

          button.className =
            'nt-bundle__option-value';

          button.dataset.ntStepIndex =
            String(stepIndex);

          button.dataset.ntOptionIndex =
            String(optionIndex);

          button.dataset.ntOptionValue =
            String(value);

          button.textContent = String(value);

          const isSelected =
            piece.selectedOptions[optionIndex] ===
            String(value);

          if (isSelected) {
            button.classList.add('is-selected');
            button.setAttribute(
              'aria-pressed',
              'true'
            );
          } else {
            button.setAttribute(
              'aria-pressed',
              'false'
            );
          }

          const available =
            this.isOptionValueAvailable(
              product,
              piece.selectedOptions,
              optionIndex,
              String(value)
            );

          if (!available) {
            button.disabled = true;
            button.classList.add(
              'is-unavailable'
            );
          }

          valuesHolder.appendChild(button);
        });

        group.appendChild(valuesHolder);
        holder.appendChild(group);
      });
    }

    normalizeValues(values) {
      if (!Array.isArray(values)) return [];

      return values
        .map((value) => {
          if (
            typeof value === 'string' ||
            typeof value === 'number'
          ) {
            return String(value);
          }

          /*
           * Fallback in case Shopify serializes an
           * ProductOptionValue object.
           */
          if (
            value &&
            typeof value === 'object'
          ) {
            return String(
              value.name ??
              value.value ??
              value.label ??
              ''
            );
          }

          return '';
        })
        .filter(Boolean);
    }

    selectOption(
      stepIndex,
      optionIndex,
      value
    ) {
      this.clearError();

      const piece = this.state[stepIndex];

      if (!piece?.product) return;

      /*
       * When an earlier option changes, reset all later options.
       * This prevents impossible combinations remaining selected.
       */
      piece.selectedOptions[optionIndex] = value;

      for (
        let index = optionIndex + 1;
        index < piece.selectedOptions.length;
        index += 1
      ) {
        piece.selectedOptions[index] = null;

        const option =
          piece.product.options?.[index];

        const values = this.normalizeValues(
          option?.values
        );

        if (values.length === 1) {
          piece.selectedOptions[index] =
            values[0];
        }
      }

      piece.variant = null;
      piece.complete = false;

      this.renderOptions(stepIndex);
      this.resolvePiece(stepIndex);
      this.updateUI();
    }

    isOptionValueAvailable(
      product,
      selectedOptions,
      optionIndex,
      candidateValue
    ) {
      const selections = [
        ...selectedOptions
      ];

      selections[optionIndex] =
        candidateValue;

      /*
       * Only use selections up to the option currently
       * being evaluated. Later selections may be reset.
       */
      const relevantSelections =
        selections.map((value, index) => {
          if (index > optionIndex) return null;
          return value;
        });

      return this.getVariants(product).some(
        (variant) => {
          if (!variant.available) return false;

          return relevantSelections.every(
            (selectedValue, index) => {
              if (!selectedValue) return true;

              return (
                String(variant.options?.[index]) ===
                String(selectedValue)
              );
            }
          );
        }
      );
    }

    /* ========================================================
       VARIANT RESOLUTION
       ======================================================== */

    getVariants(product) {
      return Array.isArray(product?.variants)
        ? product.variants
        : [];
    }

    resolvePiece(stepIndex) {
      const piece = this.state[stepIndex];

      if (!piece?.product) {
        piece.complete = false;
        piece.variant = null;
        return;
      }

      const product = piece.product;
      const options = Array.isArray(product.options)
        ? product.options
        : [];

      /*
       * Products with only the default variant.
       */
      if (options.length === 0) {
        const variant = this.getVariants(product).find(
          (item) => item.available
        );

        piece.variant = variant || null;
        piece.complete = Boolean(variant);

        this.updateSelectionSummary(stepIndex);
        return;
      }

      const allSelected =
        piece.selectedOptions.length === options.length &&
        piece.selectedOptions.every(
          (value) => Boolean(value)
        );

      if (!allSelected) {
        piece.variant = null;
        piece.complete = false;

        this.updateSelectionSummary(stepIndex);
        return;
      }

      const variant = this.getVariants(product).find(
        (item) => {
          if (!item.available) return false;

          const variantOptions =
            Array.isArray(item.options)
              ? item.options
              : [];

          return options.every(
            (_option, index) => {
              return (
                String(variantOptions[index]) ===
                String(
                  piece.selectedOptions[index]
                )
              );
            }
          );
        }
      );

      piece.variant = variant || null;
      piece.complete = Boolean(variant);

      this.updateSelectionSummary(stepIndex);
    }

    /* ========================================================
       SUMMARY
       ======================================================== */

    updateSelectionSummary(stepIndex) {
      const step = this.steps[stepIndex];

      if (!step) return;

      const summary = step.querySelector(
        '[data-nt-selection-summary]'
      );

      if (!summary) return;

      const piece = this.state[stepIndex];

      if (
        !piece.product ||
        !piece.variant ||
        !piece.complete
      ) {
        summary.hidden = true;
        return;
      }

      const image = summary.querySelector(
        '[data-nt-summary-image]'
      );

      const title = summary.querySelector(
        '[data-nt-summary-product]'
      );

      const optionsHolder = summary.querySelector(
        '[data-nt-summary-options]'
      );

      const selectedImage =
        piece.variant.image ||
        piece.product.image ||
        '';

      if (image) {
        if (selectedImage) {
          image.src = selectedImage;
          image.alt = piece.product.title || '';
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
        }
      }

      if (title) {
        title.textContent =
          piece.product.title || '';
      }

      if (optionsHolder) {
        const optionParts = [];

        const options = Array.isArray(
          piece.product.options
        )
          ? piece.product.options
          : [];

        options.forEach((option, index) => {
          const selected =
            piece.selectedOptions[index];

          if (!selected) return;

          optionParts.push(
            `${option.name}: ${selected}`
          );
        });

        optionsHolder.textContent =
          optionParts.join(' · ');
      }

      summary.hidden = false;
    }

    /* ========================================================
       STEP NAVIGATION
       ======================================================== */

    goToNextStep(stepIndex) {
      if (!this.state[stepIndex]?.complete) return;

      const nextIndex = stepIndex + 1;

      if (nextIndex >= this.quantity) return;

      this.maxUnlockedStep = Math.max(
        this.maxUnlockedStep,
        nextIndex
      );

      this.showStep(nextIndex);
    }

    showStep(index, scroll = true) {
      if (
        index < 0 ||
        index >= this.steps.length
      ) {
        return;
      }

      this.currentStep = index;

      this.steps.forEach((step, stepIndex) => {
        const active = stepIndex === index;

        step.hidden = !active;
        step.classList.toggle(
          'is-active',
          active
        );
      });

      this.updateProgress();

      if (scroll) {
        const activeStep = this.steps[index];

        if (activeStep) {
          const reduceMotion =
            window.matchMedia?.(
              '(prefers-reduced-motion: reduce)'
            )?.matches;

          requestAnimationFrame(() => {
            activeStep.scrollIntoView({
              behavior: reduceMotion
                ? 'auto'
                : 'smooth',
              block: 'nearest'
            });
          });
        }
      }
    }

    /* ========================================================
       UI STATE
       ======================================================== */

    updateUI() {
      this.state.forEach((_piece, index) => {
        this.updateStepStatus(index);
        this.updateStepButtons(index);
        this.updateSelectionSummary(index);
      });

      this.updateProgress();
      this.updateSubmitButton();
    }

    updateStepStatus(stepIndex) {
      const step = this.steps[stepIndex];

      if (!step) return;

      const status = step.querySelector(
        '[data-nt-step-status]'
      );

      if (!status) return;

      const complete =
        Boolean(this.state[stepIndex]?.complete);

      status.textContent = complete
        ? 'Completa'
        : 'Incompleta';

      status.classList.toggle(
        'is-complete',
        complete
      );
    }

    updateStepButtons(stepIndex) {
      const step = this.steps[stepIndex];

      if (!step) return;

      const next = step.querySelector(
        '[data-nt-next]'
      );

      if (next) {
        next.disabled =
          !this.state[stepIndex]?.complete;
      }
    }

    updateProgress() {
      this.progressItems.forEach(
        (item, index) => {
          const complete =
            Boolean(this.state[index]?.complete);

          const active =
            index === this.currentStep;

          item.classList.toggle(
            'is-active',
            active
          );

          item.classList.toggle(
            'is-complete',
            complete
          );

          item.disabled =
            index > this.maxUnlockedStep;

          item.setAttribute(
            'aria-current',
            active ? 'step' : 'false'
          );
        }
      );
    }

    allPiecesComplete() {
      return (
        this.state.length === this.quantity &&
        this.state.every(
          (piece) =>
            piece.complete &&
            piece.product &&
            piece.variant
        )
      );
    }

    updateSubmitButton() {
      const button = this.querySelector(
        '[data-nt-bundle-add]'
      );

      if (!button) return;

      const text = button.querySelector(
        '[data-nt-bundle-button-text]'
      );

      const complete =
        this.allPiecesComplete();

      button.disabled =
        !complete || this.isSubmitting;

      if (!text) return;

      if (this.isSubmitting) {
        text.textContent = 'ADICIONANDO...';
      } else if (complete) {
        text.textContent =
          'ADICIONAR KIT AO CARRINHO';
      } else {
        text.textContent =
          'SELECIONE TODAS AS OPÇÕES';
      }
    }

    /* ========================================================
       INVENTORY VALIDATION
       ======================================================== */

    validateBundleInventory() {
      const counts = new Map();

      this.state.forEach((piece) => {
        if (!piece.variant) return;

        const id = String(piece.variant.id);

        counts.set(
          id,
          (counts.get(id) || 0) + 1
        );
      });

      for (const [variantId, requiredQty] of counts) {
        const piece = this.state.find(
          (item) =>
            String(item.variant?.id) === variantId
        );

        if (!piece?.variant) continue;

        const variant = piece.variant;

        const tracksInventory =
          Boolean(variant.inventoryManagement);

        const preventsOverselling =
          variant.inventoryPolicy === 'deny';

        const inventoryQty =
          Number(variant.inventoryQuantity);

        if (
          tracksInventory &&
          preventsOverselling &&
          Number.isFinite(inventoryQty) &&
          inventoryQty >= 0 &&
          requiredQty > inventoryQty
        ) {
          const productName =
            piece.product?.title ||
            'uma das peças selecionadas';

          throw new Error(
            `Não há estoque suficiente de ${productName} para completar este kit.`
          );
        }
      }
    }

    /* ========================================================
       CART SUBMISSION
       ======================================================== */

    async submitBundle() {
      if (
        this.isSubmitting ||
        !this.allPiecesComplete()
      ) {
        return;
      }

      this.clearError();

      try {
        this.validateBundleInventory();
      } catch (error) {
        this.showError(error.message);
        return;
      }

      this.isSubmitting = true;

      const submitButton = this.querySelector(
        '[data-nt-bundle-add]'
      );

      if (submitButton) {
        submitButton.classList.add('is-loading');
        submitButton.disabled = true;
        submitButton.setAttribute(
          'aria-busy',
          'true'
        );
      }

      this.updateSubmitButton();

      const bundleId = this.createBundleId();

      const discountCode =
        String(
          this.bundle.discountCode || ''
        ).trim();

      const items = this.state.map(
        (piece, index) => ({
          id: Number(piece.variant.id),
          quantity: 1,
          properties: {
            _nt_bundle_id: bundleId,
            _nt_bundle_name:
              this.bundle.title ||
              `Kit ${this.quantity}`,
            _nt_bundle_position:
              String(index + 1),
            _nt_bundle_size:
              String(this.quantity),
            _nt_bundle_discount_code:
              discountCode
          }
        })
      );

      let previousDiscountCodes = [];

      try {
        await this.addItemsToCart(items);

        if (discountCode) {
          const discountResult =
            await this.applyDiscountCode(
              discountCode
            );

          previousDiscountCodes =
            discountResult.previousCodes || [];

          if (!discountResult.applied) {
            await this.removeBundleFromCart(
              bundleId
            );

            await this.restoreDiscountCodes(
              previousDiscountCodes
            );

            throw new Error(
              'O kit foi configurado corretamente, mas o desconto não pôde ser aplicado. Revise o código de desconto do kit.'
            );
          }
        }

        this.handleSuccessfulAdd();
      } catch (error) {
        console.error(
          '[NT Bundle] Add error:',
          error
        );

        this.showError(
          this.normalizeErrorMessage(error)
        );

        this.resetSubmitButton();
      }
    }

    async addItemsToCart(items) {
      const url =
        window.theme?.routes?.cart_add_url ||
        this.shopifyRoute('cart/add.js');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        body: JSON.stringify({
          items
        })
      });

      const result =
        await this.safeJson(response);

      if (!response.ok || result?.status) {
        const message =
          result?.description ||
          result?.message ||
          'Não foi possível adicionar o kit ao carrinho.';

        throw new Error(
          this.stringifyApiMessage(message)
        );
      }

      return result;
    }

    /* ========================================================
       DISCOUNT
       ======================================================== */

    async applyDiscountCode(code) {
      const cartBefore =
        await this.getCartJson();

      const previousCodes =
        this.getApplicableDiscountCodes(
          cartBefore
        );

      const alreadyApplied =
        previousCodes.some(
          (existingCode) =>
            existingCode.toLowerCase() ===
            code.toLowerCase()
        );

      if (alreadyApplied) {
        return {
          applied: true,
          previousCodes
        };
      }

      const combinedCodes = [
        ...previousCodes,
        code
      ];

      const response =
        await this.updateCartDiscounts(
          combinedCodes
        );

      const appliedCodes =
        this.getApplicableDiscountCodes(
          response
        );

      const applied =
        appliedCodes.some(
          (appliedCode) =>
            appliedCode.toLowerCase() ===
            code.toLowerCase()
        );

      return {
        applied,
        previousCodes
      };
    }

    async restoreDiscountCodes(codes) {
      try {
        await this.updateCartDiscounts(
          Array.isArray(codes) ? codes : []
        );
      } catch (error) {
        console.warn(
          '[NT Bundle] Could not restore previous discount codes:',
          error
        );
      }
    }

    async updateCartDiscounts(codes) {
      const url =
        window.theme?.routes?.cart_update_url ||
        this.shopifyRoute('cart/update.js');

      const discount =
        Array.isArray(codes)
          ? codes.filter(Boolean).join(',')
          : String(codes || '');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        body: JSON.stringify({
          discount
        })
      });

      const result =
        await this.safeJson(response);

      if (!response.ok) {
        throw new Error(
          result?.description ||
          result?.message ||
          'Não foi possível aplicar o desconto.'
        );
      }

      return result;
    }

    getApplicableDiscountCodes(cart) {
      const codes =
        Array.isArray(cart?.discount_codes)
          ? cart.discount_codes
          : [];

      return codes
        .filter(
          (discount) =>
            discount &&
            discount.applicable !== false &&
            discount.code
        )
        .map((discount) =>
          String(discount.code)
        );
    }

    /* ========================================================
       BUNDLE ROLLBACK
       ======================================================== */

    async removeBundleFromCart(bundleId) {
      try {
        const cart = await this.getCartJson();

        const updates = {};

        const items = Array.isArray(cart?.items)
          ? cart.items
          : [];

        items.forEach((item) => {
          const itemBundleId =
            item?.properties?._nt_bundle_id;

          if (
            String(itemBundleId) ===
            String(bundleId)
          ) {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) {
          return;
        }

        const url =
          window.theme?.routes?.cart_update_url ||
          this.shopifyRoute(
            'cart/update.js'
          );

        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json',
            'X-Requested-With':
              'XMLHttpRequest'
          },
          body: JSON.stringify({
            updates
          })
        });
      } catch (error) {
        console.error(
          '[NT Bundle] Rollback error:',
          error
        );
      }
    }

    /* ========================================================
       CART HELPERS
       ======================================================== */

    async getCartJson() {
      const response = await fetch(
        this.shopifyRoute('cart.js'),
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          'Não foi possível consultar o carrinho.'
        );
      }

      return response.json();
    }

    shopifyRoute(path) {
      const root =
        window.Shopify?.routes?.root || '/';

      return `${root}${path}`.replace(
        /([^:]\/)\/+/g,
        '$1'
      );
    }

    async safeJson(response) {
      try {
        return await response.json();
      } catch (_error) {
        return {};
      }
    }

    stringifyApiMessage(message) {
      if (typeof message === 'string') {
        return message;
      }

      if (
        message &&
        typeof message === 'object'
      ) {
        return Object.values(message)
          .flat()
          .join(' ');
      }

      return String(message || '');
    }

    /* ========================================================
       SUCCESS
       ======================================================== */

    handleSuccessfulAdd() {
      const submitButton = this.querySelector(
        '[data-nt-bundle-add]'
      );

      if (submitButton) {
        submitButton.classList.remove(
          'is-loading'
        );

        submitButton.classList.add(
          'is-success'
        );

        submitButton.removeAttribute(
          'aria-busy'
        );
      }

      this.isSubmitting = false;
      this.updateSubmitButton();

      /*
       * Palo Alto cart drawer.
       * theme.js listens for theme:cart:refresh,
       * fetches api-cart-items and opens the drawer.
       */
      const cartDrawer = document.querySelector(
        'cart-element[data-cart-drawer]'
      );

      if (cartDrawer) {
        document.dispatchEvent(
          new CustomEvent(
            'theme:cart:refresh',
            {
              bubbles: true
            }
          )
        );
      } else {
        const cartUrl =
          window.theme?.routes?.cart_url ||
          this.shopifyRoute('cart');

        window.location.href = cartUrl;
        return;
      }

      window.setTimeout(() => {
        if (!submitButton) return;

        submitButton.classList.remove(
          'is-success'
        );

        this.updateSubmitButton();
      }, 1200);
    }

    resetSubmitButton() {
      this.isSubmitting = false;

      const submitButton = this.querySelector(
        '[data-nt-bundle-add]'
      );

      if (submitButton) {
        submitButton.classList.remove(
          'is-loading',
          'is-success'
        );

        submitButton.removeAttribute(
          'aria-busy'
        );
      }

      this.updateSubmitButton();
    }

    createBundleId() {
      if (
        window.crypto &&
        typeof window.crypto.randomUUID ===
          'function'
      ) {
        return `nt-${this.bundle.productId}-${window.crypto.randomUUID()}`;
      }

      return [
        'nt',
        this.bundle.productId,
        Date.now(),
        Math.random()
          .toString(36)
          .slice(2, 10)
      ].join('-');
    }

    /* ========================================================
       ERROR HANDLING
       ======================================================== */

    normalizeErrorMessage(error) {
      const message =
        typeof error?.message === 'string'
          ? error.message
          : '';

      if (!message) {
        return 'Não foi possível adicionar o kit ao carrinho. Tente novamente.';
      }

      /*
       * More customer-friendly wording for common Shopify
       * inventory responses.
       */
      const normalized =
        message.toLowerCase();

      if (
        normalized.includes('sold out') ||
        normalized.includes('esgot')
      ) {
        return 'Uma das peças selecionadas está esgotada. Escolha outra opção.';
      }

      if (
        normalized.includes('inventory') ||
        normalized.includes('estoque') ||
        normalized.includes(
          'all 0 items'
        )
      ) {
        return 'Não há estoque suficiente para uma das peças selecionadas. Revise as opções do kit.';
      }

      return message;
    }

    showError(message) {
      if (!this.errorsElement) return;

      this.errorsElement.innerHTML = '';

      const error = document.createElement('div');

      error.className = 'errors';
      error.textContent = message;

      this.errorsElement.appendChild(error);

      this.errorsElement.classList.add(
        'is-visible'
      );

      requestAnimationFrame(() => {
        this.errorsElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      });
    }

    clearError() {
      if (!this.errorsElement) return;

      this.errorsElement.innerHTML = '';

      this.errorsElement.classList.remove(
        'is-visible'
      );
    }

    showConfigurationError() {
      console.error(
        '[NT Bundle] Bundle configuration is incomplete.'
      );
    }
  }

  customElements.define('nt-bundle', NTBundle);
})();